import { userIsReadOnly } from "../../auth/AuthService";
import { TableHeader } from "../../common/table/DotLegalTable.types";
import { ApplicationUser } from "../users/User.types";
import { Case, CaseType } from "./Case.types";
import { CaseSummarization } from "./CaseSummarizationPage";

const createTableHeader = <T>(
    property: keyof T,
    text: string,
    sortable: boolean = true,
    align: "left" | "center" = "left",
    showOnMobile: true | undefined = true
): TableHeader<T> => ({
    property,
    text,
    align,
    showOnMobile,
    notSortable: !sortable,
});

const getDefaultCaseTableHeaders = (caseType: CaseType): TableHeader<Case>[] => [
    createTableHeader<Case>("name", caseType === CaseType.Watches ? "Watch" : caseType),
    createTableHeader<Case>("image", caseType === CaseType.Trademark || caseType === CaseType.Watches ? "Fig. Mark" : "Image"),
    createTableHeader<Case>("caseNo", "Case No."),
    createTableHeader<Case>("matterId", "Matter Id"),
    createTableHeader<Case>("registrationNo", "Registration NO."),
    createTableHeader<Case>("country", "Country"),
    createTableHeader<Case>("classes", "Classes", false),
];

const getCaseTableHeadersByCaseType = (caseType: CaseType): TableHeader<Case>[] => {
    const defaultHeaders = [
        ...getDefaultCaseTableHeaders(caseType),
        createTableHeader<Case>("status", "Status"),
        createTableHeader<Case>("applicant", "Applicant"),
        createTableHeader<Case>("applicationDate", "Application Date"),
    ];

    switch (caseType) {
        case CaseType.Trademark:
            defaultHeaders.splice(defaultHeaders.length - 1, 0, createTableHeader<Case>("useDate", "Use Date"));
            return defaultHeaders;
        case CaseType.Copyright:
            return defaultHeaders.filter((h) => h.property !== "classes");
        default:
            return defaultHeaders;
    }
};

const getDashboardCaseTableHeaders = (caseType: CaseType): TableHeader<Case>[] => [
    createTableHeader<Case>("name", caseType === CaseType.Watches ? "Watch" : caseType),
    createTableHeader<Case>("image", caseType === CaseType.Trademark || caseType === CaseType.Watches ? "Fig. Mark" : "Image"),
    createTableHeader<Case>("country", "Country"),
    createTableHeader<Case>("renewalDate", caseType === CaseType.Copyright ? "Expiry Date" : "Renewal Date"),
    createTableHeader<Case>("matterId", "Matter Id"),
];

const getDashboardRenewalCaseTableHeaders = (caseType: CaseType): TableHeader<Case>[] => [
    createTableHeader<Case>("name", caseType === CaseType.Watches ? "Watch" : caseType),
    createTableHeader<Case>("image", caseType === CaseType.Trademark || caseType === CaseType.Watches ? "Fig. Mark" : "Image"),
    createTableHeader<Case>("country", "Country"),
    createTableHeader<Case>("renewalDate", caseType === CaseType.Copyright ? "Expiry Date" : "Renewal Date"),
    createTableHeader<Case>("renewalStatus", "Renewal Status"),
];

const getDaskboardRenewalCaseTableHeadersWithoutStatus = (caseType: CaseType): TableHeader<Case>[] => [
    createTableHeader<Case>("name", caseType === CaseType.Watches ? "Watch" : caseType),
    createTableHeader<Case>("image", caseType === CaseType.Trademark || caseType === CaseType.Watches ? "Fig. Mark" : "Image"),
    createTableHeader<Case>("country", "Country"),
    createTableHeader<Case>("renewalDate", caseType === CaseType.Copyright ? "Expiry Date" : "Renewal Date"),
];

const getRenewalsCaseTableHeaders = (caseType: CaseType): TableHeader<Case>[] => [
    ...getDefaultCaseTableHeaders(caseType),
    createTableHeader<Case>("applicant", "Applicant"),
    createTableHeader<Case>("renewalDate", "Renewal Date"),
    createTableHeader<Case>("renewalStatus", "Renewal Status", false),
    createTableHeader<Case>("renewalRemark", "Renewal Remark", false),
];

const getRenewalsCaseTableHeadersWithPrice = (caseType: CaseType, userIsReadOnly: boolean): TableHeader<Case>[] => {
    const result: TableHeader<Case>[] = [...getDefaultCaseTableHeaders(caseType)];

    if (!userIsReadOnly) {
        result.push(createTableHeader<Case>("price", "Estimated renewal price"));
    }

    result.push(createTableHeader<Case>("applicant", "Applicant"));
    result.push(createTableHeader<Case>("renewalDate", "Renewal Date"));
    result.push(createTableHeader<Case>("renewalStatus", "Renewal Status", false));
    if (!userIsReadOnly) {
        result.push(createTableHeader<Case>("renewalRemark", "Renewal Remark", false));
    }

    return result;
};

const getRenewalsCaseTableHeadersWithPriceOnly = (caseType: CaseType, userIsReadOnly: boolean): TableHeader<Case>[] => {
    const result: TableHeader<Case>[] = [...getDefaultCaseTableHeaders(caseType)];

    if (!userIsReadOnly) {
        result.push(createTableHeader<Case>("price", "Estimated renewal price"));
    }

    result.push(createTableHeader<Case>("applicant", "Applicant"));
    result.push(createTableHeader<Case>("renewalDate", "Renewal Date"));

    return result;
};

const getRenewalsCaseTableHeadersWithStatusAndRemarkOnly = (caseType: CaseType, userIsReadOnly: boolean): TableHeader<Case>[] => {
    let headers = [
        ...getDefaultCaseTableHeaders(caseType),
        createTableHeader<Case>("applicant", "Applicant"),
        createTableHeader<Case>("renewalDate", "Renewal Date"),
        createTableHeader<Case>("renewalStatus", "Renewal Status", false),
    ];

    if (!userIsReadOnly) {
        headers.push(createTableHeader<Case>("renewalRemark", "Renewal Remark", false));
    }
    return headers;
};

const getDisputesCaseTableHeaders = (caseType: CaseType): TableHeader<Case>[] => [
    createTableHeader<Case>("name", caseType === CaseType.Watches ? "Watch" : caseType),
    createTableHeader<Case>("image", caseType === CaseType.Trademark || caseType === CaseType.Watches ? "Fig. Mark" : "Image"),
    createTableHeader<Case>("type", "Case Type"),
    createTableHeader<Case>("dispute", "Dispute against"),
    createTableHeader<Case>("matterId", "Matter Id"),
    createTableHeader<Case>("country", "Country"),
    createTableHeader<Case>("classes", "Classes", false),
    createTableHeader<Case>("disputeDecision", "Status"),
    createTableHeader<Case>("applicationNo", "Application NO.", false),
    createTableHeader<Case>("dateOfOrder", "Case creation date"),
    createTableHeader<Case>("counterParty", "Counterparty"),
];

const getRenewalTableHeadersWithoutRegistrationActive = (caseType: CaseType): TableHeader<Case>[] => [
    ...getDefaultCaseTableHeaders(caseType),
    createTableHeader<Case>("applicant", "Applicant"),
    createTableHeader<Case>("renewalDate", "Renewal Date"),
];

const getWatchesTableHeaders = (): TableHeader<Case>[] => [
    createTableHeader<Case>("name", "Watch"),
    createTableHeader<Case>("image", "Fig. Mark"),
    createTableHeader<Case>("caseNo", "Case No."),
    createTableHeader<Case>("matterId", "Matter Id"),
    createTableHeader<Case>("country", "Country"),
    createTableHeader<Case>("classes", "Classes", false),
    createTableHeader<Case>("status", "Status", false),
    createTableHeader<Case>("applicant", "Applicant"),
    createTableHeader<Case>("renewalDate", "Expiry Date"),
];

const getCaseTableHeaders = (
    caseType: CaseType,
    showRenewalPrice: boolean,
    renewalRegistrationActive: boolean,
    key: CaseSummarization | undefined,
    verifiedUser: ApplicationUser | null
) => {
    switch (key) {
        case CaseSummarization.Dashboard:
            return caseType !== CaseType.Copyright
                ? !renewalRegistrationActive
                    ? getDaskboardRenewalCaseTableHeadersWithoutStatus(caseType)
                    : getDashboardRenewalCaseTableHeaders(caseType)
                : getDashboardCaseTableHeaders(caseType);
        case CaseSummarization.Renewals: {
            if (!renewalRegistrationActive && !showRenewalPrice) return getRenewalTableHeadersWithoutRegistrationActive(caseType);
            if (renewalRegistrationActive && showRenewalPrice) return getRenewalsCaseTableHeadersWithPrice(caseType, userIsReadOnly(verifiedUser));
            if (showRenewalPrice) return getRenewalsCaseTableHeadersWithPriceOnly(caseType, userIsReadOnly(verifiedUser));
            if (renewalRegistrationActive) return getRenewalsCaseTableHeadersWithStatusAndRemarkOnly(caseType, userIsReadOnly(verifiedUser));
            return getRenewalsCaseTableHeaders(caseType);
        }
        case CaseSummarization.Disputes:
            return getDisputesCaseTableHeaders(caseType);
        case CaseSummarization.Watches:
            return getWatchesTableHeaders();
        default:
            return getCaseTableHeadersByCaseType(caseType);
    }
};

export default getCaseTableHeaders;
